/**
 * Shared color palette for task statuses.
 *
 * Tailwind class names are statically detected, so dynamic strings like
 * `bg-${color}-500` would be purged from the build. We map every status
 * color the operator can pick (in TaskStatusesSection / TAG_COLORS) to
 * a complete record of Tailwind class fragments — every consumer
 * (kanban columns, list status badges, the round done checkbox, the
 * Completed quick-filter pill) reads from this same source so changing
 * a color in Settings actually moves the colour everywhere.
 *
 * IMPORTANT: this list MUST stay in sync with `TAG_COLORS` in
 * `src/lib/constants.ts` — that's the catalogue exposed by the color
 * picker. Adding a new tag color without adding a palette here means
 * the surface silently falls back to slate.
 */

export interface StatusPalette {
  /** Kanban column 3-px top border. */
  borderClass: string;
  /** 8-px round dot on the column header / status badge. */
  dotClass: string;
  /** Drag-over highlight for the column body. */
  hoverBgClass: string;
  /** Soft tinted surface — used for count badges and the rest-state
   *  background of status badges in the list view. */
  softBgClass: string;
  /** Text color paired with `softBgClass`. */
  softTextClass: string;
  /** Standalone accent text — used by the "+" button hover state on
   *  matching kanban columns. */
  accentTextClass: string;
  /** Filled surface for "checked" round checkboxes — saturated bg + a
   *  matching border so the box reads as a single coloured chip. */
  fillBgClass: string;
  fillBorderClass: string;
  /** Hover / pre-checked state for the round checkbox: a soft halo
   *  (border + bg + text accent) that matches the configured status
   *  color, replacing the previously-hardcoded emerald. */
  hoverBorderClass: string;
  hoverSoftBgClass: string;
  hoverTextClass: string;
}

/** All statuses share a common shape; only the color name varies.
 *  Generating the strings inline lets Tailwind's content scanner pick
 *  them up at build time. */
function buildPalette(c: string): StatusPalette {
  return {
    borderClass: `border-${c}-400`,
    dotClass: `bg-${c}-500`,
    hoverBgClass: `bg-${c}-50/50`,
    softBgClass: `bg-${c}-100`,
    softTextClass: `text-${c}-700`,
    accentTextClass: `text-${c}-500`,
    fillBgClass: `bg-${c}-500`,
    fillBorderClass: `border-${c}-500`,
    hoverBorderClass: `hover:border-${c}-400`,
    hoverSoftBgClass: `hover:bg-${c}-50`,
    hoverTextClass: `hover:text-${c}-500`,
  };
}

// Slate is the universal fallback. Declared as a literal so its classes
// definitely make it into the build — Tailwind cannot statically infer
// strings produced by `buildPalette` above.
const SLATE: StatusPalette = {
  borderClass: "border-slate-300",
  dotClass: "bg-slate-400",
  hoverBgClass: "bg-slate-50",
  softBgClass: "bg-slate-100",
  softTextClass: "text-slate-700",
  accentTextClass: "text-slate-500",
  fillBgClass: "bg-slate-500",
  fillBorderClass: "border-slate-500",
  hoverBorderClass: "hover:border-slate-400",
  hoverSoftBgClass: "hover:bg-slate-50",
  hoverTextClass: "hover:text-slate-500",
};

/* The Tailwind class strings for every supported color have to appear
 * somewhere the content scanner can see them. `buildPalette` emits
 * template literals which Tailwind v3 cannot statically detect, so we
 * include this comment as a safelist of explicit class names that the
 * scanner *will* find:
 *
 * border-red-400 border-red-500 bg-red-500 bg-red-100 bg-red-50 bg-red-50/50 text-red-700 text-red-500 hover:border-red-400 hover:bg-red-50 hover:text-red-500
 * border-orange-400 border-orange-500 bg-orange-500 bg-orange-100 bg-orange-50 bg-orange-50/50 text-orange-700 text-orange-500 hover:border-orange-400 hover:bg-orange-50 hover:text-orange-500
 * border-amber-400 border-amber-500 bg-amber-500 bg-amber-100 bg-amber-50 bg-amber-50/50 text-amber-700 text-amber-500 hover:border-amber-400 hover:bg-amber-50 hover:text-amber-500
 * border-yellow-400 border-yellow-500 bg-yellow-500 bg-yellow-100 bg-yellow-50 bg-yellow-50/50 text-yellow-700 text-yellow-500 hover:border-yellow-400 hover:bg-yellow-50 hover:text-yellow-500
 * border-lime-400 border-lime-500 bg-lime-500 bg-lime-100 bg-lime-50 bg-lime-50/50 text-lime-700 text-lime-500 hover:border-lime-400 hover:bg-lime-50 hover:text-lime-500
 * border-green-400 border-green-500 bg-green-500 bg-green-100 bg-green-50 bg-green-50/50 text-green-700 text-green-500 hover:border-green-400 hover:bg-green-50 hover:text-green-500
 * border-emerald-400 border-emerald-500 bg-emerald-500 bg-emerald-100 bg-emerald-50 bg-emerald-50/50 text-emerald-700 text-emerald-500 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-500
 * border-teal-400 border-teal-500 bg-teal-500 bg-teal-100 bg-teal-50 bg-teal-50/50 text-teal-700 text-teal-500 hover:border-teal-400 hover:bg-teal-50 hover:text-teal-500
 * border-cyan-400 border-cyan-500 bg-cyan-500 bg-cyan-100 bg-cyan-50 bg-cyan-50/50 text-cyan-700 text-cyan-500 hover:border-cyan-400 hover:bg-cyan-50 hover:text-cyan-500
 * border-blue-400 border-blue-500 bg-blue-500 bg-blue-100 bg-blue-50 bg-blue-50/50 text-blue-700 text-blue-500 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500
 * border-indigo-400 border-indigo-500 bg-indigo-500 bg-indigo-100 bg-indigo-50 bg-indigo-50/50 text-indigo-700 text-indigo-500 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-500
 * border-violet-400 border-violet-500 bg-violet-500 bg-violet-100 bg-violet-50 bg-violet-50/50 text-violet-700 text-violet-500 hover:border-violet-400 hover:bg-violet-50 hover:text-violet-500
 * border-purple-400 border-purple-500 bg-purple-500 bg-purple-100 bg-purple-50 bg-purple-50/50 text-purple-700 text-purple-500 hover:border-purple-400 hover:bg-purple-50 hover:text-purple-500
 * border-fuchsia-400 border-fuchsia-500 bg-fuchsia-500 bg-fuchsia-100 bg-fuchsia-50 bg-fuchsia-50/50 text-fuchsia-700 text-fuchsia-500 hover:border-fuchsia-400 hover:bg-fuchsia-50 hover:text-fuchsia-500
 * border-pink-400 border-pink-500 bg-pink-500 bg-pink-100 bg-pink-50 bg-pink-50/50 text-pink-700 text-pink-500 hover:border-pink-400 hover:bg-pink-50 hover:text-pink-500
 * border-rose-400 border-rose-500 bg-rose-500 bg-rose-100 bg-rose-50 bg-rose-50/50 text-rose-700 text-rose-500 hover:border-rose-400 hover:bg-rose-50 hover:text-rose-500
 */
const PALETTES: Record<string, StatusPalette> = {
  red: buildPalette("red"),
  orange: buildPalette("orange"),
  amber: buildPalette("amber"),
  yellow: buildPalette("yellow"),
  lime: buildPalette("lime"),
  green: buildPalette("green"),
  emerald: buildPalette("emerald"),
  teal: buildPalette("teal"),
  cyan: buildPalette("cyan"),
  blue: buildPalette("blue"),
  indigo: buildPalette("indigo"),
  violet: buildPalette("violet"),
  purple: buildPalette("purple"),
  fuchsia: buildPalette("fuchsia"),
  pink: buildPalette("pink"),
  rose: buildPalette("rose"),
  gray: SLATE,
  slate: SLATE,
};

/** Resolve a status color name (as stored in `TaskStatusConfig.color`)
 *  to its full palette. Unknown / null colors fall back to slate so
 *  the surface still renders cleanly. */
export function paletteFor(
  color: string | null | undefined,
): StatusPalette {
  if (!color) return SLATE;
  return PALETTES[color] ?? SLATE;
}
